import { Link } from 'react-router-dom';
import { Avatar, Badge, Caption1, makeStyles, mergeClasses } from '@fluentui/react-components';
import { personDisplayName, type LifecycleState, type Person } from '@vigeo/shared';
import { KeyValueList, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { ReadinessBadge, directoryLine } from '@/features/readiness';
import { lifecyclePresenters } from './lifecycle.js';
import type { ReadinessEntry } from './usePeopleReadiness.js';

/**
 * Shared presentation pieces for the directory (checkpoint 7B): the identity link, the
 * stage tag, the readiness cell and the card that the Board and the phone List both use.
 * Only supported facts appear — name, role, stage, the engine's verdict and its next step.
 */

const useStyles = makeStyles({
  /** Reference card (Figma 153:4248): flat white, radius 16, padding 16, no border, tinted shadow. */
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    minWidth: 0,
  },
  identity: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, minWidth: 0 },
  who: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** Identity in the section-title voice (16/600); the name is the link, not the whole card. */
  name: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': { textDecorationLine: 'underline' },
    ':focus-visible': { textDecorationLine: 'underline' },
    /** On phones the name is the card's tap target, so it meets the 44px rule itself. */
    [shellMedia.phone]: { display: 'inline-flex', alignItems: 'center', minHeight: shellMetrics.touchTarget },
  },
  nameCompact: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  role: { color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  /** Tags keep the small radius (checkpoint 7A). */
  tag: { borderRadius: tokens.borderRadiusSmall },
  /** Two lines at most; the full sentence is on the person's page. */
  next: {
    color: tokens.colorNeutralForeground2,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  quiet: { color: tokens.colorNeutralForeground3 },
});

export function StageTag({ state }: { readonly state: LifecycleState }) {
  const s = useStyles();
  return (
    <Badge appearance="outline" size="medium" shape="rounded" className={s.tag}>
      {lifecyclePresenters[state].label}
    </Badge>
  );
}

/** The verdict when the engine has answered; an honest placeholder when it has not. */
export function ReadinessCell({ entry }: { readonly entry: ReadinessEntry }) {
  const s = useStyles();
  if (entry.status === 'ready') return <ReadinessBadge state={entry.checklist.state} />;
  return <Caption1 className={s.quiet}>{entry.status === 'untracked' ? 'Not tracked yet' : entry.status === 'pending' ? 'Deriving…' : 'Unavailable'}</Caption1>;
}

/** The blocker count and next HR action, or why there is none yet. */
export function nextLine(entry: ReadinessEntry): string {
  if (entry.status === 'ready') return directoryLine(entry.checklist);
  if (entry.status === 'untracked') return 'Compliance is not tracked for this person yet.';
  return entry.status === 'pending' ? 'Deriving readiness from the checklist…' : 'Readiness could not be derived right now.';
}

export interface PersonNameProps {
  readonly person: Person;
  readonly compact?: boolean;
  /** Where the name leads when it is not the person's hiring record: the Roster sends it to the employee profile (#585). */
  readonly to?: string;
  /**
   * A plain click opens something over the page instead of following the link, and is handed the link itself so the keyboard
   * can be given back to it. The link stays a real one: a click with Ctrl, Cmd, Shift or the middle button still follows it.
   */
  readonly onOpen?: (person: Person, opener: HTMLElement) => void;
}

export function PersonName({ person, compact = false, to, onOpen }: PersonNameProps) {
  const s = useStyles();
  return (
    <Link
      to={to ?? `/app/people/${person.id}`}
      className={mergeClasses(s.name, compact && s.nameCompact)}
      title={personDisplayName(person.name)}
      aria-haspopup={onOpen === undefined ? undefined : 'dialog'}
      onClick={onOpen === undefined ? undefined : (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onOpen(person, event.currentTarget);
      }}
    >
      {personDisplayName(person.name)}
    </Link>
  );
}

export interface PersonCardProps {
  readonly person: Person;
  readonly role: string;
  readonly entry: ReadinessEntry;
}

export function PersonCard({ person, role, entry }: PersonCardProps) {
  const s = useStyles();
  const display = personDisplayName(person.name);
  const line = nextLine(entry);
  return (
    <article className={s.card} aria-label={display}>
      <div className={s.identity}>
        <Avatar name={display} size={56} color="neutral" aria-hidden />
        <div className={s.who}>
          <PersonName person={person} />
          <Caption1 className={s.role}>{role}</Caption1>
        </div>
      </div>
      <KeyValueList
        aria-label={`${display} status`}
        items={[
          { label: 'Stage', value: <StageTag state={person.lifecycle} /> },
          { label: 'Readiness', value: <ReadinessCell entry={entry} /> },
        ]}
      />
      <Caption1 className={s.next} title={line}>
        {line}
      </Caption1>
    </article>
  );
}
